import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';

type AudioState = 'idle' | 'playing' | 'paused' | 'error';

interface AudioPlayerProps {
  title: string;
  src: string;
  ctaLabel?: string;
  brandColor?: string;
  durationHintSec?: number;
  subtitle?: string;
  className?: string;
  id?: string;
}

export default function AudioPlayer({ 
  title, 
  src, 
  ctaLabel = "🎧 Les voix de Cosmos Wave", 
  brandColor = "hsl(85, 40%, 50%)",
  durationHintSec,
  subtitle,
  className = "",
  id
}: AudioPlayerProps) {
  const [state, setState] = useState<AudioState>('idle');
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element once on component mount
  useEffect(() => {
    // Utilisation de l'élément audio HTML du DOM au lieu de new Audio()
    if (audioRef.current) {
      const audio = audioRef.current;
      
      // Event listeners for state management
      const handlePlay = () => setState('playing');
      const handlePause = () => setState('paused');
      const handleEnded = () => {
        setState('idle');
        setProgress(0);
      };
      const handleTimeUpdate = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      const handleError = (e: Event) => {
        console.error('Audio error:', e);
        setState('error');
      };
      
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('error', handleError);

      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('error', handleError);
        audio.pause();
      };
    }
  }, [src]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    const audio = audioRef.current;
    
    if (state === 'playing') {
      audio.pause();
    } else {
      audio.play().catch((error) => {
        console.error('Playbook failed:', error);
        setState('error');
      });
    }
  };

  // Handle document visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && audioRef.current && state === 'playing') {
        audioRef.current.pause();
      }
    };

    const handleBeforeUnload = () => {
      if (audioRef.current && state === 'playing') {
        audioRef.current.pause();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    return mins > 0 ? `${mins} min` : `${seconds} sec`;
  };

  // Voice wave animation component avec vibration pendant lecture
  function VoiceWaveIcon({ isPlaying }: { isPlaying: boolean }) {
    return (
      <div className="flex items-center gap-1 h-8 w-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={`voice-wave-bar bg-primary rounded-sm w-1 transition-all duration-300 ${
              isPlaying ? 'animate-pulse' : 'h-4'
            }`}
            style={{
              height: isPlaying ? 'var(--bar-height, 16px)' : '16px',
              animationDelay: `${i * 0.1}s`,
              animation: isPlaying ? `voice-wave-${i + 1} 0.6s ease-in-out infinite alternate` : 'none'
            }}
          />
        ))}
      </div>
    );
  }

  // Fonction pour formater le temps en mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Élément audio HTML avec toutes les propriétés demandées */}
      <audio
        ref={audioRef}
        id={id}
        className="scroll-mt-24"
        controls
        preload="metadata"
        crossOrigin="anonymous"
        src={src}
        style={{ display: 'none' }} // Caché car on utilise notre UI custom
      >
        Votre navigateur ne supporte pas l&apos;audio HTML5.
      </audio>
      
      <style>{`
        .voice-wave-bar {
          animation-duration: 1.2s;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }
        .voice-wave-bar:nth-child(1) { animation-name: voice-wave-1; animation-delay: 0s; }
        .voice-wave-bar:nth-child(2) { animation-name: voice-wave-2; animation-delay: 0.1s; }
        .voice-wave-bar:nth-child(3) { animation-name: voice-wave-3; animation-delay: 0.2s; }
        .voice-wave-bar:nth-child(4) { animation-name: voice-wave-4; animation-delay: 0.3s; }
        
        @keyframes voice-wave-1 {
          0% { height: 12px; }
          100% { height: 32px; }
        }
        @keyframes voice-wave-2 {
          0% { height: 16px; }
          100% { height: 28px; }
        }
        @keyframes voice-wave-3 {
          0% { height: 20px; }
          100% { height: 36px; }
        }
        @keyframes voice-wave-4 {
          0% { height: 18px; }
          100% { height: 24px; }
        }
      `}</style>

      {/* Refined header with elegant typography */}
      <div className="space-y-4">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-2">
          <div className="w-2 h-2 rounded-full bg-primary mr-2 animate-pulse" />
          <span className="text-primary font-medium text-sm">Les voix de Cosmos Wave</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent leading-tight">
          FAQ : Les réponses essentielles en 5 minutes
        </h2>
        {subtitle && (
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Enhanced audio control button */}
      <div className="flex flex-col items-center gap-4">
        <Button
          onClick={togglePlayPause}
          variant="outline"
          size="lg"
          disabled={state === 'error'}
          className="group relative overflow-hidden bg-gradient-to-r from-card via-card to-card/90 hover:from-primary/5 hover:via-primary/10 hover:to-primary/5 border-2 border-primary/30 hover:border-primary/50 rounded-2xl px-8 py-4 h-auto shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
        >
          {/* Subtle glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <div className="relative flex items-center gap-4">
            {state === 'error' ? (
              <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center">
                <span className="text-destructive text-sm">!</span>
              </div>
            ) : (
              <div className="relative">
                <VoiceWaveIcon isPlaying={state === 'playing'} />
                {state === 'playing' && (
                  <div className="absolute -inset-2 bg-primary/20 rounded-full animate-ping opacity-30" />
                )}
              </div>
            )}
            
            <div className="flex flex-col items-start text-left">
              <span className="font-semibold text-lg text-foreground group-hover:text-primary/90 transition-colors">
                {state === 'error' ? 'Audio non disponible' : ctaLabel}
              </span>
            </div>
          </div>
        </Button>
        
        {state === 'error' && (
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Fichier audio temporairement indisponible
            </p>
            <p className="text-xs text-muted-foreground/70">
              Nous mettons à jour notre système d'hébergement audio
            </p>
          </div>
        )}
      </div>
      
      {/* Enhanced progress bar avec timeline centrée */}
      {(state === 'playing' || state === 'paused') && progress > 0 && audioRef.current && (
        <div className="w-full max-w-lg mx-auto space-y-3">
          <div className="relative w-full bg-primary/10 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-primary via-primary/90 to-primary transition-all duration-300 relative" 
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse" />
            </div>
          </div>
          <div className="flex justify-center text-sm text-muted-foreground">
            <span>
              {formatTime(audioRef.current.currentTime)} / {formatTime(audioRef.current.duration || 0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}